const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const levelMeta = {
    excellent: { label: '優秀', color: '#15803d', tag: 'success' },
    good: { label: '良好', color: '#15803d', tag: 'processing' },
    caution: { label: '需注意', color: '#b45309', tag: 'warning' },
    risk: { label: '偏高風險', color: '#dc2626', tag: 'error' }
};

const scoreDebtRatio = (ratio) => {
    if (ratio === null) return 70;
    if (ratio <= 30) return 95;
    if (ratio <= 50) return 78;
    if (ratio <= 70) return 55;
    return 30;
};

const scoreSavingsRate = (rate) => {
    if (rate === null) return 60;
    if (rate >= 20) return 95;
    if (rate >= 10) return 78;
    if (rate >= 0) return 55;
    return 25;
};

const scoreNetAsset = (netAssetValue) => {
    if (netAssetValue > 0) return 90;
    if (netAssetValue === 0) return 55;
    return 20;
};

const scoreCashFlow = (monthlyCashFlow) => {
    if (monthlyCashFlow > 0) return 90;
    if (monthlyCashFlow === 0) return 55;
    return 25;
};

const toLevel = (score) => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'caution';
    return 'risk';
};

const formatPercent = (value) => {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${value.toLocaleString('zh-TW', { maximumFractionDigits: 1 })}%`;
};

export function buildFinanceHealthReport(totals = {}) {
    const totalAssets = Number(totals.totalAssets ?? 0);
    const totalLiabilities = Number(totals.totalLiabilities ?? 0);
    const netAssetValue = Number(totals.netAssetValue ?? 0);
    const monthlyIncome = Number(totals.monthlyIncome ?? 0);
    const monthlyExpenses = Number(totals.monthlyExpenses ?? 0);
    const monthlyCashFlow = Number(totals.monthlyCashFlow ?? 0);

    const debtRatio =
        totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : null;
    const savingsRate =
        monthlyIncome > 0 ? (monthlyCashFlow / monthlyIncome) * 100 : null;
    const expenseRatio =
        monthlyIncome > 0 ? (monthlyExpenses / monthlyIncome) * 100 : null;

    const dimensions = [
        {
            key: 'debt',
            title: '負債比率',
            value: debtRatio,
            display: formatPercent(debtRatio),
            score: scoreDebtRatio(debtRatio),
            hint: '負債占總資產比例，越低通常越穩健'
        },
        {
            key: 'savings',
            title: '儲蓄率',
            value: savingsRate,
            display: formatPercent(savingsRate),
            score: scoreSavingsRate(savingsRate),
            hint: '月現金流占月收入比例，反映每月結餘能力'
        },
        {
            key: 'net',
            title: '淨資產',
            value: netAssetValue,
            display:
                netAssetValue === 0
                    ? '—'
                    : netAssetValue.toLocaleString('zh-TW', {
                          maximumFractionDigits: 0
                      }),
            score: scoreNetAsset(netAssetValue),
            hint: '資產扣除負債後的淨值'
        },
        {
            key: 'cashflow',
            title: '月現金流',
            value: monthlyCashFlow,
            display:
                monthlyCashFlow === 0
                    ? '—'
                    : monthlyCashFlow.toLocaleString('zh-TW', {
                          maximumFractionDigits: 0
                      }),
            score: scoreCashFlow(monthlyCashFlow),
            hint: '每月收入扣除支出後的實際結餘'
        }
    ].map((item) => ({
        ...item,
        level: toLevel(item.score),
        ...levelMeta[toLevel(item.score)]
    }));

    const overallScore = Math.round(
        dimensions.reduce((sum, item) => sum + item.score, 0) / dimensions.length
    );
    const overallLevel = toLevel(overallScore);

    const insights = [];
    if (debtRatio !== null && debtRatio > 70) {
        insights.push('負債比率偏高，建議優先檢視高息負債與還款節奏。');
    } else if (debtRatio !== null && debtRatio > 50) {
        insights.push('負債比率中等，可持續追蹤負債結構與利率成本。');
    }

    if (monthlyCashFlow < 0) {
        insights.push('每月收支呈現赤字，建議盤點固定支出與可調整項目。');
    } else if (savingsRate !== null && savingsRate >= 20) {
        insights.push('儲蓄率表現良好，財務緩衝空間相對充足。');
    } else if (savingsRate !== null && savingsRate >= 0 && savingsRate < 10) {
        insights.push('儲蓄率偏低，可評估是否提升收入或優化支出。');
    }

    if (netAssetValue < 0) {
        insights.push('淨資產為負，建議優先建立還債與資產累積計畫。');
    } else if (netAssetValue > 0 && monthlyCashFlow >= 0) {
        insights.push('淨資產為正且月現金流穩定，整體財務基礎較穩健。');
    }

    if (!insights.length) {
        insights.push('可持續追蹤資產配置與月收支變化，維持財務透明度。');
    }

    return {
        overallScore,
        overallLevel,
        overallLabel: levelMeta[overallLevel].label,
        overallColor: levelMeta[overallLevel].color,
        overallTag: levelMeta[overallLevel].tag,
        debtRatio,
        savingsRate,
        expenseRatio,
        dimensions,
        insights: insights.slice(0, 3)
    };
}

const statusStageMeta = {
    crisis: {
        label: '財務波谷',
        hint: '優先處理負債與支出',
        symbol: '🌊',
        tone: 'crisis'
    },
    deficit: {
        label: '收支失衡',
        hint: '支出超過收入，建議盤點可調項目',
        symbol: '⚠️',
        tone: 'warning'
    },
    climbing: {
        label: '上岸中',
        hint: '方向正確，持續優化即可',
        symbol: '🧗',
        tone: 'climbing'
    },
    shore: {
        label: '已上岸',
        hint: '淨資產與月結餘穩健',
        symbol: '🏝️',
        tone: 'shore'
    }
};

export function buildFinanceStatusVisual(totals = {}) {
    const report = buildFinanceHealthReport(totals);
    const netAssetValue = Number(totals.netAssetValue ?? 0);
    const monthlyCashFlow = Number(totals.monthlyCashFlow ?? 0);
    const debtRatio = report.debtRatio;

    let stage = 'climbing';
    if (netAssetValue < 0 || (monthlyCashFlow < 0 && debtRatio !== null && debtRatio > 60)) {
        stage = 'crisis';
    } else if (monthlyCashFlow < 0) {
        stage = 'deficit';
    } else if (report.overallLevel === 'excellent' || report.overallLevel === 'good') {
        stage = 'shore';
    }

    return {
        stage,
        score: report.overallScore,
        levelLabel: report.overallLabel,
        levelColor: report.overallColor,
        ...statusStageMeta[stage]
    };
}
